import { Form, FormField, FormMessage } from '@components/ui/form';
import { useTranslation } from 'react-i18next';
import { useToast } from '@hooks/useToast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { useEffect } from 'react';
import { Switch } from '@components/ui/switch';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { FinalYardsDialogProps } from './types';
import { FormRequired } from '@components/ui/form/required';
import { ExternalScheduleActions } from '@components/schedules/dialogs/external/actions';
import { MaterialOptionsProperty } from './options';
import { useMaterialMeasurementForm } from '@forms/materials/measurement';

export default function FinalYardsDialog({
  open,
  setOpen,
  jobId,
  taskId,
  completed,
  schedule,
  initialValues,
  readOnly,
  scheduleType,
}: FinalYardsDialogProps) {
  const { t } = useTranslation('app');
  const { toast } = useToast();

  const {
    form,
    fieldArray: { fields, replace },
    onSubmit,
    locked,
  } = useMaterialMeasurementForm({
    material: { jobId, ...initialValues },
    successHandler: () => {
      setOpen(false);
      form.reset();
      toast({
        title: t('messages.success.schedule.update'),
      });
    },
  });

  const { setValue, reset, watch } = form;
  const isFinal = watch('final');

  useEffect(() => {
    if (!open || !initialValues) {
      reset();
      replace([]);
    } else {
      reset();
      setValue('final', false);
      const { options } = initialValues;
      replace(
        (options || []).map(
          ({
            id,
            name,
            estimatedQuantity,
            finalQuantity,
            includeEstimated,
            valueType,
            required,
            unit,
          }) => ({
            id: id || '',
            name: name || '',
            estimatedQuantity: estimatedQuantity?.toString() || '',
            finalQuantity: finalQuantity?.toString() || '',
            includeEstimated: Boolean(includeEstimated),
            valueType: valueType || '',
            required: Boolean(required),
            unit: unit || '',
          }),
        ),
      );
    }
  }, [open, initialValues, reset, replace, setValue]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[531px] max-h-[75vh] scrollbar-thin overflow-x-hidden overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{`${
            initialValues?.options?.length ? t('actions.edit') : ''
          } ${initialValues?.productFullName}`}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {fields.some((el) => el.includeEstimated) && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4">
                  <strong className="text-xs font-semibold text-neutral-800">
                    {t('pages.job.materials.final')}
                  </strong>
                  <FormField
                    control={form.control}
                    name="final"
                    render={({ field: { name, value, onChange } }) => (
                      <Switch
                        disabled={completed || readOnly}
                        name={name}
                        className="mb-0.5"
                        checked={value}
                        onCheckedChange={onChange}
                      />
                    )}
                  />
                </div>
                {isFinal && (
                  <div className="flex items-center gap-3 bg-blue-300 p-2.5 text-sm font-medium text-white">
                    <span>
                      <SvgConsumer className="text-xl" id="tooltip" />
                    </span>
                    <span>{t('pages.job.materials.concreteOrderTooltip')}</span>
                  </div>
                )}
              </div>
            )}
            {fields.map((el, i) => (
              <MaterialOptionsProperty
                key={el.id}
                index={i}
                record={el}
                isFinal={isFinal}
                disabled={readOnly}
              />
            ))}
            <FormRequired />
            <FormField name="root" render={() => <FormMessage />} />
            <ExternalScheduleActions
              finalYards
              setOpen={setOpen}
              locked={locked}
              completed={completed || false}
              scheduleId={schedule?.id}
              jobId={jobId}
              taskId={taskId}
              readOnly={readOnly}
              scheduleType={scheduleType}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
